import React, { useState, useRef, useEffect } from "react";
import { HiMenu } from "react-icons/hi";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../context/ThemeContext";
import { navigate, usePathname } from "../router";

const Header: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const { isDarkMode } = useTheme();
  const pathname = usePathname();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false);
      }
    };

    const handleScroll = () => {
      setMenuOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleMenuClick = () => {
    setMenuOpen(!menuOpen);
  };

  const handleMenuItemClick = (id: string) => {
    setMenuOpen(false);
    if (pathname !== "/") {
      navigate("/");
      // wait for home to mount before scrolling
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 50);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const handleContactClick = () => {
    setMenuOpen(false);
    navigate("/contact");
  };

  const showMenu = menuOpen || isHovering;

  return (
    <header className="sticky top-0 z-50 flex justify-between items-center px-6 lg:px-16 py-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
      <button
        type="button"
        onClick={() => {
          if (pathname !== "/") {
            navigate("/");
          } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        }}
        className="hover:opacity-80 transition bg-transparent border-none p-0 m-0"
        style={{ cursor: "pointer" }}
        aria-label="Domichron home"
      >
        <picture>
          <source
            media="(max-width: 639px)"
            srcSet={isDarkMode ? "/logo_darkmode.png" : "/logo_lightmode.png"}
          />
          <source
            media="(min-width: 640px)"
            srcSet={isDarkMode ? "/logo_darkmode.svg" : "/logo_lightmode.svg"}
          />
          <img
            src={isDarkMode ? "/logo_darkmode.svg" : "/logo_lightmode.svg"}
            alt="Domichron Logo"
            style={{ height: 40 }}
          />
        </picture>
      </button>

      <div className="flex items-center gap-4">
        {/* Inline nav on md+ screens */}
        <nav className="hidden md:flex items-center gap-8">
          <button
            onClick={() => handleMenuItemClick("ventures")}
            className="text-domichron-text dark:text-white hover:text-domichron-orange dark:hover:text-domichron-orange transition-colors text-base"
          >
            Ventures
          </button>
          <button
            onClick={() => handleMenuItemClick("principles")}
            className="text-domichron-text dark:text-white hover:text-domichron-orange dark:hover:text-domichron-orange transition-colors text-base"
          >
            Principles
          </button>
          <button
            onClick={() => handleMenuItemClick("about")}
            className="text-domichron-text dark:text-white hover:text-domichron-orange dark:hover:text-domichron-orange transition-colors text-base"
          >
            About
          </button>
          <button
            onClick={handleContactClick}
            className={`transition-colors text-base ${
              pathname === "/contact"
                ? "text-domichron-orange"
                : "text-domichron-text dark:text-white hover:text-domichron-orange dark:hover:text-domichron-orange"
            }`}
          >
            Contact
          </button>
        </nav>

        {/* Hamburger only on small screens */}
        <div
          className="relative md:hidden"
          ref={menuRef}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <button
            ref={buttonRef}
            onClick={handleMenuClick}
            className="text-domichron-text dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 p-2 rounded-lg transition-colors"
            aria-label="Toggle menu"
            aria-expanded={showMenu}
          >
            <HiMenu className="w-6 h-6" />
          </button>
          <AnimatePresence>
            {showMenu && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute right-0 mt-2 w-48 rounded-lg border bg-white dark:bg-gray-900 shadow-lg hover:shadow-xl transition-shadow text-lg text-gray-700 dark:text-gray-300"
              >
                <div className="py-1">
                  <button
                    onClick={() => handleMenuItemClick("ventures")}
                    className="w-full text-left px-4 py-2 text-domichron-text dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    Ventures
                  </button>
                  <button
                    onClick={() => handleMenuItemClick("principles")}
                    className="w-full text-left px-4 py-2 text-domichron-text dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    Principles
                  </button>
                  <button
                    onClick={() => handleMenuItemClick("about")}
                    className="w-full text-left px-4 py-2 text-domichron-text dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    About
                  </button>
                  <button
                    onClick={handleContactClick}
                    className="w-full text-left px-4 py-2 text-domichron-text dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    Contact
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
};

export default Header;
